import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const EyeIcon = ({ off }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {off ? (
            <>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a13.16 13.16 0 0 1-1.67 2.68" />
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 8 10 8a9.74 9.74 0 0 0 5.39-1.61" />
                <path d="M2 2l20 20" />
                <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            </>
        ) : (
            <>
                <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
                <circle cx="12" cy="12" r="3" />
            </>
        )}
    </svg>
);

const AlertIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const RouteGraphic = () => (
    <svg className="auth-brand-route" viewBox="0 0 400 500" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M40 480 C 120 380, 60 300, 160 250 S 320 160, 300 40" stroke="#F2A900" strokeWidth="3" strokeDasharray="2 14" strokeLinecap="round" />
        <circle cx="40" cy="480" r="6" fill="#F2A900" />
        <circle cx="300" cy="40" r="6" fill="#F2A900" />
    </svg>
);

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const emailRef = useRef(null);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            const response = await axios.post(`${apiUrl}/api/v1/auth/login`, { email, password });
            localStorage.setItem('tankly_token', response.data);
            navigate('/');
        } catch (err) {
            if (err.response?.status === 403 && err.response?.data?.includes('pending')) {
                setError('Your company is currently pending admin verification. Please try again later.');
            } else {
                setError('We could not sign you in. Check your email and password and try again.');
            }
            setIsLoading(false);
            emailRef.current?.focus();
        }
    };

    return (
        <div className="auth-shell">
            <section className="auth-brand" aria-label="About Tankly">
                <img className="auth-brand-logo" src="/tankly_logo.svg" alt="Tankly" />
                <RouteGraphic />
                <div className="auth-brand-copy">
                    <h1>Never overpay for fuel again.</h1>
                    <p>Tankly plots the cheapest fuel stop on every route your fleet drives — automatically.</p>
                </div>
                <div className="auth-brand-stat">
                    <div>
                        <strong>18%</strong>
                        <span>Avg. fuel cost saved</span>
                    </div>
                    <div>
                        <strong>40k+</strong>
                        <span>Stations tracked</span>
                    </div>
                </div>
            </section>

            <section className="auth-form-side">
                <div className="auth-card">
                    <h2>Welcome back</h2>
                    <p className="auth-subtitle">Sign in to access your fleet dashboard.</p>

                    {error && (
                        <div className="alert alert-error" role="alert" aria-live="assertive">
                            <AlertIcon />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleLogin} noValidate>
                        <div className="field">
                            <label htmlFor="login-email">Email address</label>
                            <input
                                id="login-email"
                                ref={emailRef}
                                name="email"
                                type="email"
                                autoComplete="email"
                                placeholder="you@company.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                aria-invalid={!!error}
                            />
                        </div>
                        <div className="field">
                            <label htmlFor="login-password">Password</label>
                            <div className="password-field">
                                <input
                                    id="login-password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    aria-invalid={!!error}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(s => !s)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    aria-pressed={showPassword}
                                >
                                    <EyeIcon off={showPassword} />
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="btn-primary" disabled={isLoading}>
                            {isLoading && <span className="spinner-icon" aria-hidden="true" />}
                            {isLoading ? 'Signing in…' : 'Sign in'}
                        </button>
                    </form>

                    <div className="auth-footer">
                        Don't have an account? <Link to="/signup">Register your company</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Login;
